import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../../components/feature/AdminLayout';
import { AiSalesSubnav, EmptyState, ErrorState, SkeletonBlock, SourceBanner } from '../components/ui';
import { fetchConversation, fetchConversations } from '../lib/repository';
import { trackAiSalesEvent } from '../lib/telemetry';
import type { Conversation, SalesChannel } from '../types/contracts';

const FILTERS = ['all', 'unread', 'active', 'hot', 'follow_up', 'human_handoff', 'won', 'lost'] as const;
const CHANNELS: Array<SalesChannel | 'all'> = ['all', 'whatsapp', 'instagram', 'facebook', 'website'];

export default function AiSalesInboxPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState<Conversation[]>([]);
  const [active, setActive] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>('all');
  const [channel, setChannel] = useState<(typeof CHANNELS)[number]>('all');

  const load = async () => {
    setLoading(true);
    setError('');
    const r = await fetchConversations();
    setLoading(false);
    if (!r.ok) { setError(r.message); return; }
    setItems(r.data.items);
  };

  useEffect(() => { void load(); }, []);

  useEffect(() => {
    if (!id) { setActive(null); return; }
    void (async () => {
      const r = await fetchConversation(id);
      if (r.ok) {
        setActive(r.data);
        trackAiSalesEvent('conversation_opened', { id });
      }
    })();
  }, [id]);

  const list = useMemo(() => items.filter((c) => {
    if (channel !== 'all' && c.channel !== channel) return false;
    if (filter === 'unread' && !c.unread) return false;
    if (filter === 'active' && c.status !== 'active') return false;
    if (filter === 'hot' && c.score < 80) return false;
    if (filter === 'follow_up' && c.status !== 'follow_up') return false;
    if (filter === 'human_handoff' && c.handoff_status !== 'HUMAN_HANDOFF') return false;
    if (filter === 'won' && c.status !== 'won') return false;
    if (filter === 'lost' && c.status !== 'lost') return false;
    if (q.trim()) {
      const hay = `${c.lead_name} ${c.business ?? ''} ${c.last_message ?? ''}`.toLowerCase();
      if (!hay.includes(q.trim().toLowerCase())) return false;
    }
    return true;
  }), [items, filter, channel, q]);

  return (
    <AdminLayout>
      <AiSalesSubnav />
      <SourceBanner />
      {loading ? <SkeletonBlock /> : null}
      {error ? <ErrorState message={error} onRetry={() => void load()} /> : null}
      {!loading && !error && items.length === 0 ? (
        <EmptyState title="No conversations yet." body="Connect a sales channel to start receiving DressnMore leads." />
      ) : null}
      {!loading && !error && items.length > 0 ? (
        <div className="grid lg:grid-cols-12 gap-3 min-h-[70vh]">
          <aside className={`lg:col-span-3 rounded-2xl bg-white border border-gray-100 overflow-hidden ${id ? 'hidden lg:block' : ''}`}>
            <div className="p-3 border-b border-gray-50 space-y-2">
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, business..." className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs" />
              <div className="flex flex-wrap gap-1">
                {FILTERS.map((f) => (
                  <button key={f} type="button" onClick={() => setFilter(f)} className={`px-2 py-1 rounded-md text-[10px] font-bold ${filter === f ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`}>{f.replace('_', ' ')}</button>
                ))}
              </div>
              <div className="flex flex-wrap gap-1">
                {CHANNELS.map((c) => (
                  <button key={c} type="button" onClick={() => setChannel(c)} className={`px-2 py-1 rounded-md text-[10px] font-bold ${channel === c ? 'bg-teal-700 text-white' : 'bg-gray-100 text-gray-600'}`}>{c}</button>
                ))}
              </div>
            </div>
            <ul className="max-h-[62vh] overflow-y-auto">
              {list.map((c) => (
                <li key={c.id}>
                  <button type="button" onClick={() => navigate(`/ai-sales/inbox/${c.id}`)} className={`w-full text-start px-3 py-3 border-b border-gray-50 ${id === c.id ? 'bg-teal-50' : ''}`}>
                    <div className="flex justify-between text-xs font-bold">
                      <span>{c.lead_name}</span>
                      <span className={c.handoff_status === 'HUMAN_HANDOFF' ? 'text-amber-700' : 'text-teal-700'}>{c.handoff_status === 'HUMAN_HANDOFF' ? 'HUMAN' : 'AI'}</span>
                    </div>
                    <p className="text-[11px] text-gray-500 truncate">{c.last_message}</p>
                  </button>
                </li>
              ))}
            </ul>
          </aside>
          <section className={`lg:col-span-6 rounded-2xl bg-white border border-gray-100 p-4 ${!id ? 'hidden lg:block' : ''}`}>
            {!active ? <p className="text-sm text-gray-400 p-6">Select a conversation</p> : (
              <>
                <button type="button" className="lg:hidden text-xs font-bold mb-2" onClick={() => navigate('/ai-sales/inbox')}>← Inbox</button>
                <div className="flex justify-between gap-2 border-b border-gray-50 pb-3 mb-3">
                  <div>
                    <h2 className="font-black">{active.lead_name}</h2>
                    <p className="text-xs text-gray-500">{active.business} · {active.channel} · {active.interested_plan || '—'}</p>
                  </div>
                  <div className="text-end">
                    <p className="text-lg font-black text-teal-700">{active.score}</p>
                    <p className="text-[10px] font-bold">{active.handoff_status}</p>
                  </div>
                </div>
                <div className="space-y-2 max-h-[52vh] overflow-y-auto">
                  {active.messages.map((m) => (
                    <div key={m.id} className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${m.author === 'customer' ? 'bg-gray-100' : m.author === 'ai' ? 'bg-teal-50 ms-auto' : m.author === 'human' ? 'bg-indigo-50 ms-auto' : 'mx-auto text-center text-xs text-gray-500 bg-transparent'}`}>
                      <p className="text-[10px] uppercase tracking-wide opacity-60">{m.author}</p>
                      <p>{m.body}</p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
          <aside className={`lg:col-span-3 rounded-2xl bg-white border border-gray-100 p-4 ${!active ? 'hidden lg:block' : ''}`}>
            {!active?.snapshot ? <p className="text-xs text-gray-400">Lead snapshot appears when known fields exist.</p> : (
              <div className="space-y-3 text-sm">
                <h3 className="font-bold">Lead snapshot</h3>
                {Object.entries({
                  Name: active.snapshot.name,
                  Phone: active.snapshot.phone,
                  Business: active.snapshot.business,
                  Country: active.snapshot.country,
                  Branches: active.snapshot.branch_count,
                  Users: active.snapshot.user_count,
                  Software: active.snapshot.current_software,
                  Pain: active.snapshot.pain_point,
                  Plan: active.snapshot.interested_plan,
                  Source: active.snapshot.source,
                }).map(([k, v]) => v == null || v === '' ? null : (
                  <div key={k}><p className="text-[10px] uppercase text-gray-400">{k}</p><p>{String(v)}</p></div>
                ))}
                {active.insight?.summary ? <div><p className="text-[10px] uppercase text-gray-400">AI summary</p><p className="text-xs">{active.insight.summary}</p></div> : null}
                {active.insight?.intent ? <div><p className="text-[10px] uppercase text-gray-400">Intent</p><p>{active.insight.intent}</p></div> : null}
                {active.insight?.recommended_action ? <div><p className="text-[10px] uppercase text-gray-400">Next action</p><p>{active.insight.recommended_action}</p></div> : null}
                <div>
                  <p className="text-[10px] uppercase text-gray-400">Lead score</p>
                  <p className="text-xl font-black">{active.score} / 100 {active.score >= 80 ? 'HOT' : ''}</p>
                </div>
              </div>
            )}
          </aside>
        </div>
      ) : null}
    </AdminLayout>
  );
}
